// Dummy questionnaires store, shared between AdminQuestionnaireList and
// AdminQuestionnaireDetail. Replace with backend-backed data in a later
// iteration.
//
// Future endpoint shapes (following the existing apiService pattern):
//
//   apiService.fetchQuestionnaires()           → GET    /api/seq/questionnaires
//   apiService.createQuestionnaire(payload)    → POST   /api/seq/questionnaires
//   apiService.updateQuestionnaire(id, patch)  → PATCH  /api/seq/questionnaires/:id
//   apiService.deleteQuestionnaire(id)         → DELETE /api/seq/questionnaires/:id
//
// Note on the description field: the seq_questionnaires schema deliberately
// does NOT include `description` ("Can be added later if Tamara wants longer
// admin-side context"). It is kept here in the form/dummy data so the UI can
// be iterated on; either add the column to the schema or strip the field from
// the form when wiring up the backend.

import { reactive } from 'vue';

const initialItems = [
  {
    id: 1,
    name: 'Swiss Eating Quiz',
    slug: 'seq-main',
    description: 'The production questionnaire used in the public quiz at seq.bfh.ch.',
    status: 'active',
    is_locked: true,
    created_at: '2026-03-15T10:00:00Z',
    updated_at: '2026-04-22T14:30:00Z',
  },
  {
    id: 2,
    name: 'Pilot Round 2 — BE Schools',
    slug: 'pilot-round-2-be-schools',
    description: 'Pilot questionnaire for the second round of school-based testing in the Canton of Bern. Adds revised wording on Hülsenfrüchte questions.',
    status: 'draft',
    is_locked: false,
    created_at: '2026-04-28T09:15:00Z',
    updated_at: '2026-05-09T11:42:00Z',
  },
  {
    id: 3,
    name: 'SEQ — 2025 Pilot',
    slug: 'seq-2025-pilot',
    description: 'First pilot questionnaire from autumn 2025. Kept for historical comparison.',
    status: 'archived',
    is_locked: true,
    created_at: '2025-09-12T08:00:00Z',
    updated_at: '2025-12-31T17:00:00Z',
  },
  {
    id: 4,
    name: 'Workshop — Spring 2026',
    slug: 'workshop-spring-2026',
    description: '',
    status: 'draft',
    is_locked: false,
    created_at: '2026-05-05T13:20:00Z',
    updated_at: '2026-05-08T16:55:00Z',
  },
];

export const questionnairesState = reactive({
  items: initialItems.slice(),
});

export function findBySlug(slug) {
  return questionnairesState.items.find(q => q.slug === slug);
}

export function addQuestionnaire(payload) {
  const nextId = questionnairesState.items.reduce((max, q) => Math.max(max, q.id), 0) + 1;
  const now = new Date().toISOString();
  const item = {
    id: nextId,
    name: payload.name,
    slug: payload.slug,
    description: payload.description || '',
    status: payload.status || 'draft',
    is_locked: false,
    created_at: now,
    updated_at: now,
  };
  questionnairesState.items.push(item);
  return item;
}

export function updateQuestionnaire(id, patch) {
  const item = questionnairesState.items.find(q => q.id === id);
  if (!item) return null;
  if (patch.name !== undefined) item.name = patch.name;
  if (patch.description !== undefined) item.description = patch.description;
  if (patch.status !== undefined) item.status = patch.status;
  // Slug is intentionally not updatable post-creation (schema doc: slugs end
  // up in URLs and exports).
  item.updated_at = new Date().toISOString();
  return item;
}

// Replace an item in the store with a server-returned row (after PATCH).
// Use this instead of updateQuestionnaire(id, patch) when you have the
// full updated row from the API — keeps timestamps and any server-only
// fields in sync.
export function replaceQuestionnaire(updated) {
  if (!updated || updated.id == null) return;
  const idx = questionnairesState.items.findIndex(q => q.id === updated.id);
  if (idx !== -1) {
    questionnairesState.items.splice(idx, 1, updated);
  }
}

// Insert-or-update by id. Used by the detail page when fetching by slug
// — if the row is already in the store (we came from the list), we
// refresh it; if it isn't (direct URL load), we add it.
export function upsertQuestionnaire(item) {
  if (!item || item.id == null) return;
  const idx = questionnairesState.items.findIndex(q => q.id === item.id);
  if (idx === -1) {
    questionnairesState.items.push(item);
  } else {
    questionnairesState.items.splice(idx, 1, item);
  }
}

export function removeQuestionnaire(id) {
  const idx = questionnairesState.items.findIndex(q => q.id === id);
  if (idx !== -1) {
    questionnairesState.items.splice(idx, 1);
    return true;
  }
  return false;
}

// Slug constraint from the schema (seq_questionnaires_slug_format CHECK):
//   ^[a-z][a-z0-9_-]{1,63}$
// Lowercase letters, digits, hyphens, or underscores. Starts with a letter.
// 2 to 64 characters total.
export const SLUG_REGEX = /^[a-z][a-z0-9_-]{1,63}$/;
